import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Stack,
  MenuItem,
  Alert,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
} from "@mui/material";
import OnboardProductFields from "./OnboardProductFields";

const PRIMARY_COLOR = "#FF03F3";
const PRIMARY_DARK = "#000080";
const containedButtonSx = {
  backgroundColor: PRIMARY_COLOR,
  color: "#fff",
  boxShadow: "none",
  "&:hover": {
    backgroundColor: PRIMARY_DARK,
    boxShadow: "none",
  },
  "&.Mui-disabled": {
    backgroundColor: "grey.400",
    color: "#f5f5f5",
  },
};
const textButtonSx = {
  color: PRIMARY_COLOR,
  fontWeight: 600,
  "&:hover": {
    backgroundColor: "rgba(0,0,160,0.08)",
  },
};

const outlinedInputSx = {
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      borderColor: "rgba(0,0,160,0.2)",
    },
    "&:hover fieldset": {
      borderColor: "rgba(0,0,160,0.5)",
    },
    "&.Mui-focused fieldset": {
      borderColor: PRIMARY_COLOR,
      borderWidth: 2,
    },
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: PRIMARY_COLOR,
  },
};

const defaultForm = {
  title: "",
  description: "",
  projectName: "",
  priority: "",
  eta: "",
  category: "NEW_FEATURE",
  productCode: "",
  productName: "",
  productOwner: "",
  productDescription: "",
  domain: "",
  subDomain: "",
  parentDomain: "",
  domainId: "",
  subDomainId: "",
  parentDomainId: "",
  ucSchema: "",
  adlsPath: "",
};

const SubmitFeatureRequestDialog = ({
  open,
  onClose,
  onSubmit,
  submitting,
  categoryOptions,
}) => {
  const [form, setForm] = useState(defaultForm);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!open) {
      setForm(defaultForm);
      setErrors({});
    }
  }, [open]);

  const isOnboardProduct = form.category === "REGISTER_PRODUCT";

  const safeTrim = (value) => (value || "").trim();
  const isValidEffemEmail = (value) =>
    /^[^\s@]+@effem\.com$/i.test((value || "").trim());
  const clearOnboardFields = (payload) => ({
    ...payload,
    productCode: "",
    productName: "",
    productOwner: "",
    productDescription: "",
    domain: "",
    subDomain: "",
    parentDomain: "",
    domainId: "",
    subDomainId: "",
    parentDomainId: "",
    ucSchema: "",
    adlsPath: "",
    uc_schema_gcp: "",
  });

  const validate = () => {
    const nextErrors = {};
    if (!safeTrim(form.title)) nextErrors.title = "Title is required";
    if (!safeTrim(form.description))
      nextErrors.description = "Description is required";
    if (isOnboardProduct) {
      if (!safeTrim(form.productName))
        nextErrors.productName = "Product name is required";
      if (!safeTrim(form.productOwner))
        nextErrors.productOwner = "Product owner email is required";
      else if (!isValidEffemEmail(form.productOwner))
        nextErrors.productOwner = "Use an @effem.com email";
      if (!safeTrim(form.productDescription))
        nextErrors.productDescription = "Product description is required";
      if (!safeTrim(form.domain)) nextErrors.domain = "Domain is required";
      if (!safeTrim(form.subDomain))
        nextErrors.subDomain = "Sub domain is required";
      if (!safeTrim(form.parentDomain))
        nextErrors.parentDomain = "Parent domain is required";
      // NEW: At least one UC schema field is required
      const hasUcSchema = safeTrim(form.ucSchema);
      const hasUcGcpSchema = safeTrim(form.uc_schema_gcp);
      const hasAdlsPath = safeTrim(form.adlsPath);
      if (!hasUcSchema && !hasUcGcpSchema && !hasAdlsPath) {
        nextErrors.ucSchema = "At least one of UC Schema, UC Schema GCP, or ADLS Path is required";
        nextErrors.uc_schema_gcp = "At least one of UC Schema, UC Schema GCP, or ADLS Path is required";
        nextErrors.adlsPath = "At least one of UC Schema, UC Schema GCP, or ADLS Path is required";
      }
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleFieldChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (field === 'ucSchema' || field === 'uc_schema_gcp' || field === 'adlsPath') {
      if (value && value.trim()) {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors.ucSchema;
          delete newErrors.uc_schema_gcp;
          delete newErrors.adlsPath;
          return newErrors;
        });
      }
    }
  };

  const handleSubmit = () => {
    if (!validate()) return;
    onSubmit(form);
  };

  const categoryLabelId = "feature-request-category-label";
  const onboardFieldsComplete = !isOnboardProduct
    ? true
    : Boolean(
      safeTrim(form.productName) &&
      safeTrim(form.productOwner) &&
      isValidEffemEmail(form.productOwner) &&
      safeTrim(form.productDescription) &&
      safeTrim(form.domain) &&
      safeTrim(form.subDomain) &&
      safeTrim(form.parentDomain) &&
      (safeTrim(form.ucSchema) || safeTrim(form.uc_schema_gcp) || safeTrim(form.adlsPath))
    );

  const baseFieldsComplete = Boolean(
    safeTrim(form.title) && safeTrim(form.description),
  );

  const canSubmit = !submitting && baseFieldsComplete && onboardFieldsComplete;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          width: { xs: "90vw", md: "50vw" },
          maxWidth: "none",
        },
      }}
    >
      <DialogTitle>Submit Feature Request</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3} mt={1}>
          <Alert
            severity="info"
            sx={{
              bgcolor: "rgba(0,0,160,0.06)",
              color: "#111827",
              borderRadius: 2,
              border: "1px solid rgba(0,0,160,0.15)",
              "& .MuiAlert-icon": { color: PRIMARY_COLOR },
            }}
          >
            <Stack spacing={0.5}>
              <Typography fontWeight={600}>
                Your feedback keeps Submit & Track alive.
              </Typography>
              <Typography variant="body2">
                Before submitting, please scan the existing feature list. If
                your idea already exists, open it and hit the like button to
                boost visibility. New insights are still welcome—just add them
                as comments on the existing feature.
              </Typography>
            </Stack>
          </Alert>

          <Box>
            <FormControl fullWidth sx={outlinedInputSx}>
              <InputLabel id={categoryLabelId}>Category *</InputLabel>
              <Select
                labelId={categoryLabelId}
                label="Category *"
                value={form.category}
                onChange={(e) => {
                  const nextCategory = e.target.value;
                  const nextForm =
                    nextCategory === "REGISTER_PRODUCT"
                      ? { ...form, category: nextCategory }
                      : clearOnboardFields({ ...form, category: nextCategory });
                  setForm(nextForm);
                  if (nextCategory !== "REGISTER_PRODUCT") {
                    setErrors((prev) => {
                      const {
                        productCode,
                        productName,
                        productOwner,
                        productDescription,
                        domain,
                        subDomain,
                        parentDomain,
                        ucSchema,
                        adlsPath,
                        ...rest
                      } = prev;
                      return rest;
                    });
                  }
                }}
                renderValue={(value) => {
                  const match = categoryOptions.find(
                    (category) => category.value === value,
                  );
                  if (!match) return "";
                  return (
                    <Stack spacing={0.25}>
                      <Typography fontWeight={600}>{match.label === "Onboard Product" ? "Onboard Product (Only for Data Pulse Team's Internal Use)" : match.label}</Typography>
                      {match.description && (
                        <Typography variant="caption" color="text.secondary">
                          {match.description}
                        </Typography>
                      )}
                    </Stack>
                  );
                }}
              >
                {categoryOptions.map((category) => (
                  <MenuItem key={category.value} value={category.value}>
                    <Stack spacing={0.25}>
                      <Typography fontWeight={600}>{category.label === "Onboard Product" ? "Onboard Product (Only for Data Pulse Team's Internal Use))" : category.label}</Typography>
                      {category.description && (
                        <Typography variant="caption" color="text.secondary">
                          {category.description}
                        </Typography>
                      )}
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>Select the type of request</FormHelperText>
            </FormControl>
          </Box>

          <Box>
            <TextField
              label="Title *"
              fullWidth
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              error={Boolean(errors.title)}
              helperText={errors.title || " "}
              inputProps={{ maxLength: 200 }}
              sx={outlinedInputSx}
            />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", textAlign: "right", mt: -2 }}
            >
              {`${form.title.length}/200 characters`}
            </Typography>
          </Box>

          <Box>
            <TextField
              label="Description *"
              multiline
              minRows={4}
              fullWidth
              value={form.description}
              onChange={(e) =>
                setForm({ ...form, description: e.target.value })
              }
              error={Boolean(errors.description)}
              helperText={errors.description}
              sx={outlinedInputSx}
            />
            <Typography variant="caption" color="text.secondary">
              Be as detailed as possible to help us understand your request
            </Typography>
          </Box>

          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <TextField
              fullWidth
              label="Project Name"
              value={form.projectName}
              onChange={(e) =>
                setForm({ ...form, projectName: e.target.value })
              }
              sx={outlinedInputSx}
            />
            <FormControl fullWidth sx={outlinedInputSx}>
              <InputLabel id="feature-priority-label">
                Priority (optional)
              </InputLabel>
              <Select
                labelId="feature-priority-label"
                value={form.priority || ""}
                label="Priority (optional)"
                onChange={(e) => setForm({ ...form, priority: e.target.value })}
              >
                <MenuItem value="">
                  <em>Not specified</em>
                </MenuItem>
                {["high", "medium", "low"].map((value) => (
                  <MenuItem key={value} value={value}>
                    {value.charAt(0).toUpperCase() + value.slice(1)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="ETA (optional)"
              type="date"
              value={form.eta}
              onChange={(e) => setForm({ ...form, eta: e.target.value })}
              InputLabelProps={{ shrink: true }}
              sx={outlinedInputSx}
            />
          </Stack>

          {isOnboardProduct && (
            <OnboardProductFields
              form={form}
              errors={errors}
              onFieldChange={handleFieldChange}
              inputSx={outlinedInputSx}
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <button
          onClick={onClose}
          className="text-[#FF03F3] border border-[#FF03F3] h-[45px] px-4 py-2.5 cursor-pointer rounded-lg"
        >
          Cancel
        </button>
        <button
          className={`${!canSubmit
              ? "bg-gray-500  cursor-not-allowed text-gray-600"
              : "bg-[#FF03F3] cursor-pointer text-white"
            }  h-[45px] px-4 py-2.5 rounded-lg`}
          onClick={handleSubmit}
          disabled={!canSubmit}
        >
          Submit Request
        </button>
      </DialogActions>
    </Dialog>
  );
};

export default SubmitFeatureRequestDialog;
